import type { ApolloQueryResult } from 'apollo-client';

import type { Metadata } from '@atlaskit/editor-common/collab';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { isCollabDraftCacheStale, markCollabDraftFetched } from './CollabDraftCacheManager.ts';
import { CollabDraftQuery } from './CollabDraftQuery.graphql.ts';
import type {
	CollabDraftQuery as CollabDraftQueryRes,
	CollabDraftQueryVariables,
} from './__types__/CollabDraftQuery.ts';

export type InitialDraft = {
	document: any;
	version: number;
	metadata?: Metadata;
};

export type FetchDraft = {
	draft: InitialDraft | null;
	response?: Response | ApolloQueryResult<any>;
	error?: Error;
};

export async function fetchDraft(contentId: string, draftShareId?: string): Promise<FetchDraft> {
	// Use cache-first only if SSR rendered AND cache is not stale (within TTL)
	// This prevents stale data during SPA transitions while still benefiting from SSR cache
	const shouldUseCache = expValEquals(
		'platform_editor_collabdraft_query_improvements',
		'isEnabled',
		true,
	)
		? window.__SSR_RENDERED__ || !isCollabDraftCacheStale(contentId, draftShareId)
		: window.__SSR_RENDERED__;

	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const response = await getApolloClient().query<CollabDraftQueryRes, CollabDraftQueryVariables>({
		query: CollabDraftQuery,
		variables: {
			id: contentId,
			hydrateAdf: true,
			...(draftShareId ? { draftShareId } : undefined),
		},
		fetchPolicy: shouldUseCache ? 'cache-first' : 'network-only',
		context: { headers: { 'X-ATL-WARM-CACHE-HINT': 'get' } },
	});

	// Mark the timestamp after fetch (whether from cache or network)
	// so subsequent fetches within TTL can use cache
	if (expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true)) {
		markCollabDraftFetched(contentId, draftShareId);
	}

	const { data, errors } = response;

	if (errors || !data.collabDraft) {
		return { draft: null, response };
	}

	const { document, version, metadata } = data.collabDraft;

	const draft = {
		version,
		metadata: excludeTypename(metadata),
		document: JSON.parse(document || ''),
	} as InitialDraft;

	return { draft, response };
}

function excludeTypename(
	metadata?: Record<string, any> | null,
): Record<string, any> | null | undefined {
	if (!metadata) return metadata;
	const { __typename, ...rest } = metadata;
	return rest;
}
